import { computed, ref, watch, onMounted, watchEffect, Ref } from "vue";
import { Participant, Project, Recipients } from "../../types/PersologOnlineTypes.ts";
import { ProfileContractorAPI } from "../../api";
import { useRoute, useRouter } from "vue-router";
import { getDisplayName, removeQueryParameter } from "../GeneralUtils.ts";
import { getPdfUrl, openPdfInNewTab } from "../PdfUtils.ts";
import { i18n } from "../../main.ts";

export default function useProfileView(participant: Ref<Participant>, project: Ref<Project>) {
    const route = useRoute();
    const router = useRouter();

    const showPdfModal = ref(false);
    const pdfUrl = ref("");
    const languages = ref<string[]>([]);
    const impersonated = localStorage.getItem("impersonatedAccount") !== null;
    const viewable = !impersonated && project.value.recipients !== Recipients.PARTICIPANT;
    const loading = ref(false);
    const isDisabled = computed(
        () =>
            !(participant.value.status === "GENERATED" || participant.value.status === "NOTIFIED") ||
            project.value.recipients === Recipients.PARTICIPANT,
    );
    const items = ref(setItems());

    // Open the PDF in a new tab without downloading it
    async function viewPdf(language: string) {
        try {
            loading.value = true;
            const { objectUrl, filename, blob } = await getPdfUrl(
                project.value.trainer as string,
                participant.value.selectedProduct,
                participant.value.id as number,
                language,
            );
            pdfUrl.value = objectUrl;
            await openPdfInNewTab(blob, filename, {
                trainer: project.value.trainer as string,
                product: participant.value.selectedProduct,
                participantId: participant.value.id as number,
                language,
            });
            loading.value = false;
        } catch (error) {
            console.error("Error displaying PDF:", error);
        } finally {
            loading.value = false;
        }
    }

    function setItems() {
        return languages.value.map((lang) => ({
            label: getDisplayName(i18n.global.locale.value, lang),
            command: async () => {
                await viewPdf(lang);
            },
        }));
    }

    const closeDialog = () => {
        showPdfModal.value = false;
        removeQueryParameter(route, router, "participantId");
        pdfUrl.value = "";
    };

    async function fetchLanguages() {
        languages.value = await ProfileContractorAPI.fetchLanguages(
            participant.value.selectedProduct,
            participant.value.id as number,
        );
    }

    watch(
        () => participant,
        async (newParticipant) => {
            if (newParticipant) {
                await fetchLanguages();
            }
        },
    );

    watch(
        () => participant.value.status,
        async (newStatus) => {
            if (newStatus === "GENERATED" || newStatus === "NOTIFIED") {
                await fetchLanguages();
            }
        },
    );

    watchEffect(async () => {
        items.value = setItems();
    });

    /**
     * When using query parameters, e.g. if a trainer clicks on the link in the email, the participantId is passed as a query parameter.
     * viewPdf function is called when the component is mounted to check if the participantId is in the query parameters and if so, display the PDF.
     * The PDF is displayed because by calling the viewPdf function, the PDF is downloaded and the showPdfModal is set to true.
     */
    onMounted(async () => {
        await fetchLanguages();
    });

    return {
        showPdfModal,
        pdfUrl,
        languages,
        viewable,
        loading,
        viewPdf,
        items,
        closeDialog,
        isDisabled,
        fetchLanguages,
    };
}
